"use client";

import { useRef, useState, useEffect, useMemo, type ReactNode } from "react";
import { Plant } from "./PlantList";
import {
  ShoppingCart,
  Mail,
  Download,
  Loader2,
  ArrowRightLeft,
  CornerDownRight,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  effectivePlantQuantity,
  formatPlantQuantity,
  QUANTITY_DISCLAIMER,
  UNKNOWN_COUNT_NOTICE,
} from "@/lib/quantity";
import { useLiveQuotes } from "@/lib/cart/useLiveQuotes";
import { CartItem } from "@/lib/cart/types";
import {
  ProductRecommendation,
  groupRecommendationsByProduct,
} from "@/lib/productRecommendations";
import { plantKeyNumbers } from "@/lib/plantKey";

interface RetailerBranding {
  companyName: string;
  logo: string;
  primaryColor: string;
}

interface DesignImages {
  spring?: string;
  summer?: string;
  autumn?: string;
  winter?: string;
}

interface CouponData {
  code?: string | null;
  purpose?: string | null;
  imageUrl?: string | null;
  minPurchaseRequirement?: number | null;
  relativeExpirationDays?: number | null;
}

interface DesignCoupon {
  couponHeading?: string | null;
  value?: number | null;
  type?: string | null;
  name?: string | null;
  code?: string | null;
  imageUrl?: string | null;
  minPurchaseRequirement?: number | null;
  relativeExpirationDays?: number | null;
}

const BARCODE_IMAGE_USED_SENTINEL = "BARCODE_IMAGE_USED";

type PillTone = "swap" | "soldOut" | "warn";

const PILL_TONE: Record<PillTone, string> = {
  swap: "bg-plantista-100 text-plantista-800 border-plantista-300 dark:bg-plantista-800 dark:text-plantista-100 dark:border-plantista-600",
  soldOut:
    "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800",
  warn: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
};

function StatusPill({
  tone,
  children,
}: {
  tone: PillTone;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide leading-none ${PILL_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

interface ShoppingListProps {
  plants: Plant[];
  quantities: Record<string, number>;
  retailerBranding?: RetailerBranding;
  retailerId?: string;
  /**
   * Tenant id for commerce (live-quote) resolution when it differs from the
   * catalog retailerId — the D2C flow browses the Plantista GC catalog but
   * prices via the D2C Shopify tenant (mirrors cart.metadata.retailerId).
   * Defaults to retailerId.
   */
  commerceTenantId?: string;
  onAddToCart?: (plants: Plant[], quantities: Record<string, number>) => void;
  onSendEmail?: () => Promise<void>;
  /**
   * Show the in-list action row (Reserve/Download + Send to My Email). Hidden
   * for the D2C flow, where these CTAs are consolidated into the "Make it
   * Happen" box rendered below the shopping list (PLA2-344). Defaults to true.
   */
  showActions?: boolean;
  sunLevel?: string;
  styleName?: string;
  zone?: string;
  designImages?: DesignImages;
  coupons?: CouponData[];
  designCoupon?: DesignCoupon | null;
  /**
   * Plants that were excluded from the generated design because they are out
   * of stock. Listed after the design's plants for reference only — never
   * priced, never counted in the subtotal, never added to the cart (PLA2-458).
   */
  unavailablePlants?: Plant[];
  /**
   * Show the "Recommended Supplies" section (on screen + PDF). Driven by the
   * tenant's `productRecommendations` feature flag — off for D2C (PLA2-764).
   * Defaults to true.
   */
  showRecommendations?: boolean;
  /**
   * The design has a planting diagram: number each row with the diagram's
   * colored key badge and list rows in key order, like the container result.
   */
  numbered?: boolean;
  /**
   * In-stock substitutes the shopper picked for sold-out recipe plants:
   * substitute plant id → the sold-out plant it replaces. Substitute rows get a
   * "Swapped in" pill plus a link back to the original; the original is not
   * listed again under "Left out".
   */
  replacedFor?: Record<string, Plant>;
}

interface CareInstruction {
  genusId: string;
  genusName: string;
  instructionText: string;
  type: string;
  previewImageUrl: string | null;
  videoUrl: string | null;
  everyNDays: number | null;
}

const DEFAULT_SUPPLIES = [
  "Premium potting soil or compost",
  "Organic mulch (2-3 inch layer)",
  "Slow-release fertilizer",
  "Garden tools (trowel, gloves)",
];

const PLANTING_TIPS = [
  "Water plants thoroughly after planting",
  'Add 2-3" mulch around plants to retain moisture',
  "Avoid planting in extreme heat or cold",
  "Dig holes twice as wide as root balls",
  "Plant at the same depth as the nursery container",
];

// Convert image URLs to base64 using server-side proxy to bypass CORS
async function fetchImagesAsBase64(
  urls: string[]
): Promise<Record<string, string | null>> {
  try {
    const response = await fetch("/api/image-proxy", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ urls }),
    });

    if (!response.ok) {
      console.error("Failed to fetch images via proxy");
      return {};
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching images:", error);
    return {};
  }
}

// Fetch product recommendations for plants
async function fetchProductRecommendations(
  genusIds: string[],
  retailerId?: string
): Promise<ProductRecommendation[]> {
  if (genusIds.length === 0) return [];

  try {
    const retailerParam = retailerId ? `&retailerId=${retailerId}` : "";
    const response = await fetch(
      `/api/product-recommendations?genusIds=${genusIds.join(",")}${retailerParam}`
    );
    if (!response.ok) {
      console.error("Failed to fetch product recommendations");
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching product recommendations:", error);
    return [];
  }
}

// Fetch care instructions for plants (planting tips)
async function fetchCareInstructions(
  genusIds: string[],
  retailerId?: string
): Promise<CareInstruction[]> {
  if (genusIds.length === 0) return [];

  try {
    const retailerParam = retailerId ? `&retailerId=${retailerId}` : "";
    const response = await fetch(
      `/api/care-instructions?genusIds=${genusIds.join(",")}${retailerParam}`
    );
    if (!response.ok) {
      console.error(
        "Failed to fetch care instructions - endpoint may not exist yet"
      );
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching care instructions:", error);
    return [];
  }
}

const DEFAULT_BRANDING: RetailerBranding = {
  companyName: "Plantista",
  logo: "/logo512.png",
  primaryColor: "#22c55e",
};

export function ShoppingList({
  plants,
  quantities,
  retailerBranding,
  retailerId,
  commerceTenantId,
  onAddToCart,
  onSendEmail,
  showActions = true,
  sunLevel,
  styleName,
  zone,
  designImages,
  coupons,
  designCoupon,
  unavailablePlants,
  showRecommendations = true,
  numbered = false,
  replacedFor = {},
}: ShoppingListProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [plantImages, setPlantImages] = useState<Record<string, string>>({});
  const [designImageBase64s, setDesignImageBase64s] = useState<
    Record<string, string>
  >({});
  const [couponImageBase64s, setCouponImageBase64s] = useState<
    Record<string, string>
  >({});
  const [designCouponImageBase64, setDesignCouponImageBase64] = useState<
    string | null
  >(null);
  const [logoBase64, setLogoBase64] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<
    ProductRecommendation[]
  >([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [careInstructions, setCareInstructions] = useState<CareInstruction[]>(
    []
  );
  const [loadingCareInstructions, setLoadingCareInstructions] = useState(false);

  // Use retailer branding or fallback to defaults
  const branding = retailerBranding || DEFAULT_BRANDING;

  // Only mention "coupon" in the email CTA when the retailer actually has one
  // (PLA2-10: vague "Send to My Email" copy).
  const hasCoupon = Boolean((coupons && coupons.length > 0) || designCoupon);
  const emailCtaLabel = "Finalize & Email Design";
  const emailCtaTitle = hasCoupon
    ? "Finalize design — email me the design, shopping list, coupon and planting info"
    : "Finalize design — email me the design, shopping list and planting info";

  // Stable key derived from plant IDs — avoids re-fetching when the array
  // reference changes but the actual plants haven't (e.g. cart edits cause re-renders).
  const plantIdsKey = useMemo(
    () =>
      plants
        .map((p) => p.id)
        .sort()
        .join(","),
    [plants]
  );

  // Live Shopify quotes (D2C, PLA2-410 spine). For PLANTISTA tenants the hook
  // resolves to empty quotes and every price below falls back to plant.price.
  const quoteItems = useMemo<CartItem[]>(
    () =>
      // Exclude plants the counter explicitly placed at 0 ("not used"): they're
      // not part of the live quote. A missing count (undefined) still → 1.
      plants
        .filter((p) => quantities[p.id] !== 0)
        .map((p) => ({
          plant: p,
          quantity: effectivePlantQuantity(quantities[p.id]),
          unitPrice: p.price ?? null,
          genusAliasId: p.id,
        })),
    [plantIdsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const {
    source,
    quotes,
    loading: quotesLoading,
  } = useLiveQuotes(quoteItems, commerceTenantId ?? retailerId);
  const isShopify = source === "SHOPIFY";

  // Per-plant live pricing/availability, mirroring CartModal's line derivation.
  const getLine = (plant: Plant) => {
    const quote = quotes[plant.id];
    const soldOut = isShopify && !!quote && quote.available === false;
    const unmapped = isShopify && !!quote && quote.variantId === null;
    // Counter said 0 → "not used". Distinct from undefined (unknown/1+ fallback).
    const notUsed = quantities?.[plant.id] === 0;
    const price = isShopify
      ? (quote?.unitPrice ?? plant.price ?? null)
      : (plant.price ?? null);
    const priceLoading = isShopify && quotesLoading && !quote;
    return {
      quote,
      soldOut,
      unmapped,
      notUsed,
      excluded: soldOut || unmapped,
      price,
      priceLoading,
    };
  };

  // Fetch product recommendations and care instructions when plants change
  useEffect(() => {
    async function loadData() {
      // Get unique genus IDs from plants (genusId field from the plant data)
      const genusIds = plants
        .map((p) => (p as any).genusId)
        .filter((id): id is string => !!id);

      console.log(
        "[ShoppingList] Plants:",
        plants.length,
        "GenusIds found:",
        genusIds.length
      );
      if (plants[0]) {
        console.log("[ShoppingList] Sample plant:", {
          id: plants[0].id,
          commonName: plants[0].commonName,
          genusId: (plants[0] as any).genusId,
          allKeys: Object.keys(plants[0]),
        });
      }

      if (genusIds.length === 0) {
        console.log("[ShoppingList] No genus IDs found - skipping fetch");
        setRecommendations([]);
        setCareInstructions([]);
        return;
      }

      const uniqueGenusIds = [...new Set(genusIds)];
      console.log(
        "[ShoppingList] Fetching recommendations for",
        uniqueGenusIds.length,
        "unique genus IDs"
      );

      // Fetch product recommendations
      if (showRecommendations) {
        setLoadingRecommendations(true);
        const recs = await fetchProductRecommendations(
          uniqueGenusIds,
          retailerId
        );
        console.log(
          "[ShoppingList] Got",
          recs.length,
          "product recommendations"
        );
        setRecommendations(recs);
        setLoadingRecommendations(false);
      }

      // Fetch care instructions (planting tips)
      setLoadingCareInstructions(true);
      const instructions = await fetchCareInstructions(
        uniqueGenusIds,
        retailerId
      );
      console.log(
        "[ShoppingList] Got",
        instructions.length,
        "care instructions"
      );
      setCareInstructions(instructions);
      setLoadingCareInstructions(false);
    }

    loadData();
  }, [plantIdsKey, retailerId, showRecommendations]);

  // Map genusId → Plant object for thumbnail lookups
  const plantByGenusId = useMemo(
    () =>
      plants.reduce(
        (acc, p) => {
          const genusId = (p as any).genusId;
          if (genusId) acc[genusId] = p;
          return acc;
        },
        {} as Record<string, Plant>
      ),
    [plantIdsKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // One entry per recommended product — with the retailer's description
  // ("why") and the design plants it applies to — split into Planting /
  // Fertilizing sections (PLA2-9).
  const productSections = useMemo(() => {
    const nameByGenusId: Record<string, string> = {};
    for (const [genusId, plant] of Object.entries(plantByGenusId)) {
      nameByGenusId[genusId] = plant.commonName;
    }
    return groupRecommendationsByProduct(recommendations, nameByGenusId);
  }, [recommendations, plantByGenusId]);

  // Out-of-stock plants that were dropped from the design. Anything that also
  // appears in `plants` is skipped so we never render a duplicate row/key.
  const unavailableRows = useMemo(() => {
    if (!unavailablePlants || unavailablePlants.length === 0) return [];
    const includedIds = new Set(plants.map((p) => p.id));
    // Originals that were swapped out are shown on their substitute's row.
    for (const [subId, original] of Object.entries(replacedFor)) {
      if (includedIds.has(subId)) includedIds.add(original.id);
    }
    return unavailablePlants.filter((p) => !includedIds.has(p.id));
  }, [unavailablePlants, plantIdsKey, replacedFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Substitutes actually in this list (a later edit may have removed one).
  const swappedCount = useMemo(
    () => plants.filter((p) => replacedFor[p.id]).length,
    [plants, replacedFor]
  );

  // Get unique product names for simple list display (fallback)
  const uniqueProductNames = [
    ...new Set(recommendations.map((r) => r.productName)),
  ].filter(Boolean);

  // Group care instructions by plant (genusId) for PDF display with plant attribution
  const careByPlant = useMemo(() => {
    const plantingOnly = careInstructions.filter(
      (i) => i.type === "planting" && i.instructionText
    );
    const source =
      plantingOnly.length > 0
        ? plantingOnly
        : careInstructions.filter((i) => i.instructionText);

    return source.reduce(
      (acc, inst) => {
        if (!acc[inst.genusId]) {
          const plant = plantByGenusId[inst.genusId];
          acc[inst.genusId] = {
            plantName: plant?.commonName || inst.genusName,
            instructions: [],
          };
        }
        if (
          !acc[inst.genusId].instructions.some(
            (i) => i.instructionText === inst.instructionText
          )
        ) {
          acc[inst.genusId].instructions.push(inst);
        }
        return acc;
      },
      {} as Record<
        string,
        { plantName: string; instructions: CareInstruction[] }
      >
    );
  }, [careInstructions, plantByGenusId]);

  // Diagram key badges (same numbers + colors as the planting diagram).
  const plantKey = useMemo(
    () => (numbered ? plantKeyNumbers(plants, quantities) : {}),
    [numbered, plants, quantities]
  );
  // In numbered mode rows read 1..N like the legend; unused plants go last.
  const listedPlants = useMemo(() => {
    if (!numbered) return plants;
    const n = (id: string) => plantKey[id]?.number ?? Number.MAX_SAFE_INTEGER;
    return [...plants].sort((a, b) => n(a.id) - n(b.id));
  }, [numbered, plants, plantKey]);

  // Check if we have any quantities data
  const hasQuantities = Object.keys(quantities).length > 0;
  // Matches what the cart is seeded with: missing count → 1, explicit 0 → 0.
  const totalItems = plants.reduce(
    (sum, p) => sum + effectivePlantQuantity(quantities[p.id]),
    0
  );

  // Build supply items for cart (excluding garden tools)
  const getSupplyCartItems = (): {
    plants: Plant[];
    quantities: Record<string, number>;
  } => {
    const SUPPLY_PRICE = 20;
    const isGardenTool = (name: string) => /garden\s*tool/i.test(name);

    let supplyNames: string[];
    if (uniqueProductNames.length > 0) {
      // Use API recommendations (flatten all product names)
      supplyNames = uniqueProductNames.filter((n) => !isGardenTool(n));
    } else {
      // Use defaults
      supplyNames = DEFAULT_SUPPLIES.filter((n) => !isGardenTool(n));
    }

    const supplyPlants: Plant[] = supplyNames.map((name, i) => ({
      id: `supply-${i}-${name.replace(/\s+/g, "-").toLowerCase()}`,
      commonName: name,
      scientificName: "",
      sunLevel: "",
      category: "supply",
      price: SUPPLY_PRICE,
    }));
    const supplyQuantities: Record<string, number> = {};
    for (const p of supplyPlants) {
      supplyQuantities[p.id] = 1;
    }
    return { plants: supplyPlants, quantities: supplyQuantities };
  };

  const formatSunLevel = (sunLevel: string) => {
    return sunLevel
      .replace(/_/g, " ")
      .replace("PART SUN PART SHADE", "Part Sun")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatZone = (z: string) =>
    z.replace(/^ZONE_/i, "Zone ").replace(/([A-Z])$/, (m) => m.toLowerCase());

  const handleDownload = async () => {
    if (!printRef.current || isGenerating) return;

    setIsGenerating(true);

    try {
      // Ensure recommendations are loaded before generating PDF
      const genusIds = plants
        .map((p) => (p as any).genusId)
        .filter((id): id is string => !!id);

      if (
        showRecommendations &&
        genusIds.length > 0 &&
        recommendations.length === 0
      ) {
        console.log(
          "[ShoppingList] Fetching recommendations before PDF generation..."
        );
        const uniqueGenusIds = [...new Set(genusIds)];
        const [recs, instructions] = await Promise.all([
          fetchProductRecommendations(uniqueGenusIds, retailerId),
          fetchCareInstructions(uniqueGenusIds, retailerId),
        ]);
        setRecommendations(recs);
        setCareInstructions(instructions);
        console.log(
          "[ShoppingList] Got",
          recs.length,
          "recommendations and",
          instructions.length,
          "care instructions"
        );
        // Wait for React to re-render with new data
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Pre-load all images (plants + retailer logo + design images) as base64 via server proxy to avoid CORS issues
      const imageUrls = plants
        .filter((plant) => plant.imageUrl)
        .map((plant) => plant.imageUrl as string);

      // Also include the retailer logo if it's an external URL
      const logoUrl = branding.logo;
      const isExternalLogo = logoUrl.startsWith("http");
      if (isExternalLogo) {
        imageUrls.push(logoUrl);
      }

      // Include design season images
      const seasonKeys = ["spring", "summer", "autumn", "winter"] as const;
      for (const season of seasonKeys) {
        const url = designImages?.[season];
        if (url) imageUrls.push(url);
      }

      // Include coupon barcode images
      if (coupons) {
        for (const c of coupons) {
          if (!c.code && c.imageUrl) imageUrls.push(c.imageUrl);
        }
      }

      // Include design coupon image (designed marketing version)
      if (designCoupon?.imageUrl) {
        imageUrls.push(designCoupon.imageUrl);
      }

      const urlToBase64 = await fetchImagesAsBase64(imageUrls);

      // Map plant IDs to their base64 images
      const imagesMap: Record<string, string> = {};
      plants.forEach((plant) => {
        if (plant.imageUrl && urlToBase64[plant.imageUrl]) {
          imagesMap[plant.id] = urlToBase64[plant.imageUrl] as string;
        }
      });
      setPlantImages(imagesMap);

      // Map season images to base64
      const diBase64: Record<string, string> = {};
      for (const season of seasonKeys) {
        const url = designImages?.[season];
        if (url && urlToBase64[url]) {
          diBase64[season] = urlToBase64[url] as string;
        }
      }
      setDesignImageBase64s(diBase64);

      // Map coupon barcode images to base64
      const ciBase64: Record<string, string> = {};
      if (coupons) {
        for (const c of coupons) {
          if (!c.code && c.imageUrl && urlToBase64[c.imageUrl]) {
            ciBase64[c.imageUrl] = urlToBase64[c.imageUrl] as string;
          }
        }
      }
      setCouponImageBase64s(ciBase64);

      // Map design coupon image to base64
      if (designCoupon?.imageUrl && urlToBase64[designCoupon.imageUrl]) {
        setDesignCouponImageBase64(
          urlToBase64[designCoupon.imageUrl] as string
        );
      } else {
        setDesignCouponImageBase64(null);
      }

      // Set the proxied logo
      if (isExternalLogo && urlToBase64[logoUrl]) {
        setLogoBase64(urlToBase64[logoUrl] as string);
      } else {
        setLogoBase64(null);
      }

      // Wait for state update and re-render
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Render content with html2canvas, then paginate by slicing the canvas
      // into page-sized chunks. This reliably constrains content to page width
      // (pdf.html() has known scaling bugs in jsPDF 4.x that blow up font sizes).
      printRef.current.style.display = "block";

      const contentEl = printRef.current.firstElementChild as HTMLElement;

      const canvas = await html2canvas(contentEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Measure "don't split" zones (cards/groups) in canvas pixels.
      // Must run while contentEl is still visible.
      const htmlToCanvasScale = canvas.width / contentEl.offsetWidth;
      const contentTop = contentEl.getBoundingClientRect().top;
      const avoidRanges: Array<[number, number]> = Array.from(
        contentEl.querySelectorAll<HTMLElement>("[data-pdf-avoid-break]")
      )
        .map((el) => {
          const r = el.getBoundingClientRect();
          return [
            (r.top - contentTop) * htmlToCanvasScale,
            (r.bottom - contentTop) * htmlToCanvasScale,
          ] as [number, number];
        })
        .sort((a, b) => a[0] - b[0]);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const margin = 10;
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const usableWidth = pdfWidth - margin * 2;
      const usableHeight = pdfHeight - margin * 2;

      // Scale: fit canvas width to usable page width
      const scale = usableWidth / canvas.width;
      const fullImgHeight = canvas.height * scale;

      if (fullImgHeight <= usableHeight) {
        // Fits on one page
        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableWidth,
          fullImgHeight
        );
      } else {
        // Slice canvas vertically into page-sized chunks, snapping cut
        // points to the gap above any card that would otherwise be split.
        const pageHeightPx = Math.floor(usableHeight / scale);

        // If a Y-coordinate would split a card, return the card's top
        // (so we cut just above it instead).
        const snapAboveCard = (y: number, floorY: number): number => {
          for (const [top, bottom] of avoidRanges) {
            // Tiny tolerance so a cut exactly on a boundary isn't treated
            // as "inside" the range.
            if (y > top + 0.5 && y < bottom - 0.5) {
              const candidate = Math.floor(top);
              // Only snap if it leaves forward progress; otherwise the
              // card is taller than a page and we have to cut through it.
              if (candidate > floorY) return candidate;
            }
          }
          return y;
        };

        let sourceY = 0;
        let isFirstPage = true;

        while (sourceY < canvas.height) {
          const maxEnd = Math.min(sourceY + pageHeightPx, canvas.height);
          const endY =
            maxEnd >= canvas.height ? maxEnd : snapAboveCard(maxEnd, sourceY);
          const sliceHeightPx = endY - sourceY;
          if (sliceHeightPx <= 0) break;

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeightPx;
          const ctx = pageCanvas.getContext("2d");
          if (!ctx) break;

          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, -sourceY);

          const sliceImgHeight = sliceHeightPx * scale;

          if (!isFirstPage) pdf.addPage();
          pdf.addImage(
            pageCanvas.toDataURL("image/png"),
            "PNG",
            margin,
            margin,
            usableWidth,
            sliceImgHeight
          );

          sourceY = endY;
          isFirstPage = false;
        }
      }

      printRef.current.style.display = "none";

      const date = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const filePrefix = branding.companyName
        .toLowerCase()
        .replace(/\s+/g, "-");
      pdf.save(
        `${filePrefix}-shopping-list-${date.replace(/\s+/g, "-").toLowerCase()}.pdf`
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating your PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with retailer branding */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img
            src={branding.logo}
            alt={branding.companyName}
            className="h-12 w-auto object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
          <h2 className="text-2xl font-bold text-plantista-950 dark:text-white font-display">
            Shopping List
          </h2>
        </div>
        <span className="text-sm text-plantista-600 dark:text-plantista-400">
          {hasQuantities
            ? `${totalItems} total plants`
            : `${plants.length} plant types`}
        </span>
      </div>

      {(sunLevel || styleName || zone) && (
        <div className="flex flex-wrap items-center gap-2">
          {sunLevel && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              ☀️ {formatSunLevel(sunLevel)}
            </span>
          )}
          {styleName && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-plantista-50 dark:bg-plantista-900 text-plantista-700 dark:text-plantista-300 border border-plantista-200 dark:border-plantista-700">
              🌿 {styleName}
            </span>
          )}
          {zone && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              📍 {formatZone(zone)}
            </span>
          )}
        </div>
      )}

      {swappedCount > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-plantista-200 bg-plantista-50 px-4 py-3 dark:border-plantista-700 dark:bg-plantista-900/60">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-plantista-200 text-plantista-800 dark:bg-plantista-700 dark:text-plantista-100">
            <ArrowRightLeft className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-plantista-900 dark:text-plantista-100">
              {swappedCount === 1
                ? "1 sold-out plant was swapped"
                : `${swappedCount} sold-out plants were swapped`}{" "}
              for in-stock look-alikes
            </p>
            <p className="text-xs text-plantista-600 dark:text-plantista-400">
              Your design already uses the replacements, so everything below is
              ready to buy.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg divide-y divide-gray-200 dark:divide-plantista-800">
        {listedPlants.map((plant) => {
          // A missing count (vision counter failed) shows "Count unavailable"
          // — never a number (PLA2-763). Explicit 0 shows "Not used".
          const quantity = quantities[plant.id];
          const hasQuantity = quantity !== undefined && quantity > 0;
          const unknownCount = quantity === undefined;
          const { soldOut, unmapped, notUsed, excluded, price, priceLoading } =
            getLine(plant);
          const lineTotal =
            hasQuantity && !excluded && price !== null
              ? price * quantity
              : null;
          const key = plantKey[plant.id];
          const replaced = replacedFor[plant.id];

          return (
            <div
              key={plant.id}
              className={
                replaced
                  ? "bg-plantista-50/50 dark:bg-plantista-900"
                  : undefined
              }
            >
              <div
                className={`p-4 flex items-center justify-between gap-4${
                  notUsed ? " opacity-60" : ""
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  {numbered &&
                    (key ? (
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: key.color }}
                        aria-label={`Number ${key.number} on the planting diagram`}
                      >
                        {key.number}
                      </span>
                    ) : (
                      <span className="h-7 w-7 shrink-0" aria-hidden />
                    ))}
                  {plant.imageUrl ? (
                    <img
                      src={plant.imageUrl}
                      alt={plant.commonName}
                      className="w-12 h-12 rounded object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded bg-gray-200 dark:bg-plantista-800"></div>
                  )}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="font-semibold text-plantista-950 dark:text-white">
                        {plant.commonName}
                      </p>
                      {replaced && !soldOut && (
                        <StatusPill tone="swap">
                          <ArrowRightLeft className="h-3 w-3" aria-hidden />
                          Swapped in
                        </StatusPill>
                      )}
                      {soldOut && (
                        <StatusPill tone="soldOut">Sold out</StatusPill>
                      )}
                      {unmapped && !soldOut && (
                        <StatusPill tone="warn">Not online</StatusPill>
                      )}
                    </div>
                    <p className="text-sm text-plantista-600 dark:text-plantista-400 italic">
                      {plant.scientificName}
                    </p>
                    <p
                      className={`text-xs mt-0.5 ${
                        unmapped
                          ? "text-amber-600 dark:text-amber-400"
                          : soldOut
                            ? "text-red-600 dark:text-red-400"
                            : "text-plantista-500 dark:text-plantista-400"
                      }`}
                    >
                      {notUsed
                        ? "Not used in this design"
                        : priceLoading
                          ? "Checking price…"
                          : unmapped
                            ? "Not available online"
                            : soldOut
                              ? "Not purchasable right now"
                              : price !== null
                                ? `$${price.toFixed(2)} each`
                                : "Price TBD"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p
                    className={
                      notUsed || unknownCount
                        ? "text-sm font-semibold text-plantista-500 dark:text-plantista-400"
                        : "text-lg font-bold text-plantista-600 dark:text-plantista-300"
                    }
                  >
                    {notUsed
                      ? "Not used"
                      : hasQuantity
                        ? `×${Math.round(quantity)}`
                        : formatPlantQuantity(quantity)}
                  </p>
                  {lineTotal !== null && (
                    <p className="text-sm text-plantista-500 dark:text-plantista-400">
                      ${lineTotal.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              {replaced && (
                // Lineage: which sold-out plant this one stands in for.
                <div
                  className={`-mt-2 flex items-center gap-2 pb-3 pr-4 text-xs text-plantista-600 dark:text-plantista-400 ${
                    numbered ? "pl-[6.25rem]" : "pl-[4.25rem]"
                  }`}
                >
                  <CornerDownRight
                    className="h-3.5 w-3.5 shrink-0 text-plantista-400 dark:text-plantista-500"
                    aria-hidden
                  />
                  <span>Replaces</span>
                  {replaced.imageUrl ? (
                    <img
                      src={replaced.imageUrl}
                      alt=""
                      className="h-6 w-6 shrink-0 rounded object-cover grayscale opacity-70"
                    />
                  ) : null}
                  <span className="truncate font-medium text-plantista-700 line-through decoration-plantista-400/70 dark:text-plantista-300">
                    {replaced.commonName}
                  </span>
                  <StatusPill tone="soldOut">Sold out</StatusPill>
                </div>
              )}
            </div>
          );
        })}

        {/* Out-of-stock plants — excluded from the design, not purchasable. */}
        {unavailableRows.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 dark:bg-plantista-950/40">
            <p className="text-[11px] font-bold uppercase tracking-wide text-plantista-500 dark:text-plantista-400">
              Left out of this design
            </p>
          </div>
        )}
        {unavailableRows.map((plant) => (
          <div
            key={plant.id}
            className="p-4 flex items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4 min-w-0">
              {numbered && <span className="h-7 w-7 shrink-0" aria-hidden />}
              {plant.imageUrl ? (
                <img
                  src={plant.imageUrl}
                  alt={plant.commonName}
                  className="w-12 h-12 rounded object-cover grayscale opacity-60"
                />
              ) : (
                <div className="w-12 h-12 rounded bg-gray-200 dark:bg-plantista-800"></div>
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="font-semibold text-gray-500 dark:text-gray-400">
                    {plant.commonName}
                  </p>
                  <StatusPill tone="soldOut">Sold out</StatusPill>
                </div>
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  {plant.scientificName}
                </p>
                <p className="text-xs mt-0.5 text-plantista-500 dark:text-plantista-400">
                  Not in your design or your total
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-plantista-400 dark:text-plantista-500">
                —
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Quantity disclaimer — compact single line, under the plant list and
          above the subtotal (PLA2-344). */}
      {plants.some((p) => quantities[p.id] === undefined) && (
        <p className="text-xs leading-snug text-center text-amber-700 dark:text-amber-400 !my-2">
          {UNKNOWN_COUNT_NOTICE}
        </p>
      )}
      {plants.length > 0 && (
        <p className="text-[11px] leading-snug text-center text-plantista-500 dark:text-plantista-400 !my-2">
          {QUANTITY_DISCLAIMER}
        </p>
      )}

      {/* Subtotal — effective (live) price when Shopify, else plant.price.
          Hidden when nothing is purchasable (all sold out / unmapped). */}
      {plants.some((p) => {
        const line = getLine(p);
        return !line.excluded && line.price != null;
      }) && (
        <div className="flex justify-between items-center px-4 py-3 bg-white dark:bg-plantista-900 border border-gray-200 dark:border-plantista-800 rounded-lg">
          <span className="font-semibold text-plantista-900 dark:text-plantista-100">
            Estimated Subtotal
          </span>
          <span className="text-lg font-bold text-plantista-600 dark:text-plantista-300">
            $
            {plants
              .reduce((sum, p) => {
                const qty = effectivePlantQuantity(quantities[p.id]);
                const { excluded, price } = getLine(p);
                return sum + (!excluded && price != null ? price * qty : 0);
              }, 0)
              .toFixed(2)}
          </span>
        </div>
      )}

      {showRecommendations && (
        <div className="border border-gray-200 dark:border-plantista-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-plantista-800 border-b border-gray-200 dark:border-plantista-700">
            <h3 className="font-semibold text-plantista-900 dark:text-plantista-100 text-sm">
              Recommended Supplies
            </h3>
          </div>

          {loadingRecommendations ? (
            <div className="px-4 py-3">
              <p className="text-sm text-plantista-500 dark:text-plantista-400">
                Loading recommendations...
              </p>
            </div>
          ) : productSections.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-plantista-700">
              {productSections.map((section) => (
                <div key={section.triggerType}>
                  <div className="px-4 pt-3 pb-1 flex items-baseline justify-between gap-2">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-plantista-600 dark:text-plantista-400">
                      {section.title}
                    </h4>
                    <span className="text-[11px] text-plantista-500 dark:text-plantista-400">
                      {section.products.length}{" "}
                      {section.products.length === 1 ? "item" : "items"}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-plantista-800">
                    {section.products.map((product) => (
                      <div
                        key={`${section.triggerType}-${product.productId || product.productName}`}
                        className="px-4 py-3 flex gap-3"
                      >
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.productName}
                            className="w-10 h-10 rounded object-cover flex-shrink-0"
                          />
                        ) : null}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-plantista-950 dark:text-white">
                            {product.productName}
                          </p>
                          {product.description && (
                            <p className="text-xs leading-relaxed text-plantista-700 dark:text-plantista-300 mt-0.5">
                              {product.description}
                            </p>
                          )}
                          {product.plantNames.length > 0 && (
                            <p className="text-xs text-plantista-500 dark:text-plantista-400 mt-1">
                              <span className="font-medium">For:</span>{" "}
                              {product.plantNames.join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="px-4 py-3 space-y-1 text-sm text-plantista-700 dark:text-plantista-300">
              {DEFAULT_SUPPLIES.map((supply, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-plantista-400 flex-shrink-0" />
                  {supply}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showActions && (
        <div className="flex gap-3">
          {onAddToCart && isShopify ? (
            <button
              onClick={() => {
                const supplies = getSupplyCartItems();
                onAddToCart([...plants, ...supplies.plants], {
                  ...quantities,
                  ...supplies.quantities,
                });
              }}
              disabled={plants.length === 0}
              className="flex-1 py-3 bg-plantista-500 dark:bg-plantista-400 text-plantista-950 dark:text-plantista-950 font-bold rounded-lg hover:bg-plantista-600 dark:hover:bg-plantista-300 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-5 h-5" />
              Reserve My Plants
            </button>
          ) : (
            <button
              onClick={handleDownload}
              disabled={plants.length === 0 || isGenerating}
              className="flex-1 py-3 text-plantista-950 font-bold rounded-lg transition-all bg-plantista-400 dark:bg-plantista-400 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download Shopping List
                </>
              )}
            </button>
          )}
          {onSendEmail && (
            <button
              onClick={async () => {
                if (isSending) return;
                setIsSending(true);
                try {
                  await onSendEmail();
                } catch (err) {
                  console.error("[ShoppingList] Email send failed:", err);
                } finally {
                  setIsSending(false);
                }
              }}
              disabled={plants.length === 0 || isSending}
              title={emailCtaTitle}
              aria-label={emailCtaTitle}
              className="flex-1 py-3 bg-white dark:bg-plantista-900 text-plantista-950 dark:text-plantista-100 font-bold rounded-lg border border-gray-200 dark:border-plantista-700 hover:bg-gray-50 dark:hover:bg-plantista-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <span className="flex flex-col items-start leading-tight">
                    <span>{emailCtaLabel}</span>
                    <span className="text-xs font-normal text-plantista-600 dark:text-plantista-300">
                      Email me the design, shopping list
                      {hasCoupon ? ", coupon" : ""} & planting info
                    </span>
                  </span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Hidden printable content for PDF generation */}
      <div
        ref={printRef}
        style={{ display: "none", position: "absolute", left: "-9999px" }}
      >
        <div
          style={{
            width: "800px",
            padding: "20px",
            backgroundColor: "#ffffff",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          {/* Header */}
          <div
            data-pdf-section="header"
            style={{
              borderBottom: `3px solid ${branding.primaryColor}`,
              paddingBottom: "20px",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "8px",
              }}
            >
              <img
                src={logoBase64 || branding.logo}
                alt={branding.companyName}
                style={{
                  height: "60px",
                  width: "auto",
                  maxWidth: "250px",
                  objectFit: "contain",
                }}
              />
            </div>
            <p
              style={{
                fontSize: "18px",
                color: "#4b5563",
                margin: "8px 0 0 0",
              }}
            >
              Your Garden Shopping List
            </p>
            {(sunLevel || styleName || zone) && (
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  margin: "10px 0 4px 0",
                  lineHeight: "1",
                }}
              >
                {sunLevel && (
                  <span style={{ color: "#b45309" }}>
                    ☀️ {formatSunLevel(sunLevel)}
                  </span>
                )}
                {sunLevel && styleName && (
                  <span style={{ color: "#d1d5db", margin: "0 8px" }}>•</span>
                )}
                {styleName && (
                  <span style={{ color: "#15803d" }}>🌿 {styleName}</span>
                )}
                {(sunLevel || styleName) && zone && (
                  <span style={{ color: "#d1d5db", margin: "0 8px" }}>•</span>
                )}
                {zone && (
                  <span style={{ color: "#1d4ed8" }}>
                    📍 {formatZone(zone)}
                  </span>
                )}
              </p>
            )}
            <p
              style={{
                fontSize: "14px",
                color: "#9ca3af",
                margin: "4px 0 0 0",
              }}
            >
              Generated:{" "}
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {/* Design Images Section — summer is the always-present baseline;
              spring only exists in all-seasons mode. Gate on any season. */}
          {(designImages?.summer ||
            designImages?.spring ||
            designImageBase64s["summer"] ||
            designImageBase64s["spring"]) && (
            <div data-pdf-section="images" style={{ marginBottom: "30px" }}>
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "12px",
                  paddingBottom: "8px",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                YOUR DESIGN
              </h2>
              {/* Season images in a 2x2 grid */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "24px",
                  justifyContent: "center",
                }}
              >
                {(["spring", "summer", "autumn", "winter"] as const).map(
                  (season) => {
                    const url =
                      designImageBase64s[season] || designImages?.[season];
                    if (!url) return null;
                    const labels: Record<string, string> = {
                      spring: "Spring",
                      summer: "Summer",
                      autumn: "Autumn",
                      winter: "Winter",
                    };
                    return (
                      <div
                        key={season}
                        data-pdf-avoid-break
                        style={{ width: "calc(50% - 12px)" }}
                      >
                        <img
                          src={url}
                          alt={`${season} design`}
                          style={{
                            width: "100%",
                            borderRadius: "6px",
                            display: "block",
                            marginBottom: "4px",
                          }}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "11px",
                            color: "#6b7280",
                            margin: 0,
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {labels[season]}
                        </p>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {/* Plants Section */}
          <div data-pdf-section="plants" style={{ marginBottom: "30px" }}>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color: "#1f2937",
                marginBottom: "16px",
                paddingBottom: "8px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              YOUR PLANTS ({totalItems} total)
            </h2>

            {plants.map((plant) => {
              const quantity = quantities[plant.id];

              return (
                <div
                  key={plant.id}
                  data-pdf-avoid-break
                  style={{
                    display: "flex",
                    gap: "16px",
                    padding: "16px",
                    marginBottom: "12px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                  }}
                >
                  {/* Plant Image */}
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                      backgroundColor: "#e5e7eb",
                    }}
                  >
                    {(plantImages[plant.id] || plant.imageUrl) && (
                      <img
                        src={plantImages[plant.id] || plant.imageUrl}
                        alt={plant.commonName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </div>

                  {/* Plant Info */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div>
                        <h3
                          style={{
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#1f2937",
                            margin: "0 0 4px 0",
                          }}
                        >
                          {plant.commonName}
                          <span
                            style={{
                              marginLeft: "8px",
                              fontSize: "14px",
                              fontWeight: "normal",
                              color:
                                quantity === 0 || quantity === undefined
                                  ? "#6b7280"
                                  : "#22c55e",
                            }}
                          >
                            {quantity === 0
                              ? "(not used)"
                              : quantity === undefined
                                ? "(count unavailable)"
                                : `(${formatPlantQuantity(quantity)})`}
                          </span>
                        </h3>
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#6b7280",
                            fontStyle: "italic",
                            margin: "0 0 8px 0",
                          }}
                        >
                          {plant.scientificName}
                        </p>
                      </div>
                    </div>

                    {/* Care Info */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "12px",
                        fontSize: "13px",
                        color: "#4b5563",
                      }}
                    >
                      <span>☀️ {formatSunLevel(plant.sunLevel)}</span>
                      {plant.waterNeeds && (
                        <span>
                          💧{" "}
                          {plant.waterNeeds.charAt(0).toUpperCase() +
                            plant.waterNeeds.slice(1).toLowerCase()}
                        </span>
                      )}
                      {(plant.height || plant.width) && (
                        <span>
                          📏 {plant.height}
                          {plant.height && plant.width ? " × " : ""}
                          {plant.width}
                        </span>
                      )}
                      {plant.zones && plant.zones.length > 0 && (
                        <span>🌡️ Zones {plant.zones.join(", ")}</span>
                      )}
                    </div>

                    {/* Care Instructions */}
                    {plant.careInstructions && (
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          margin: "8px 0 0 0",
                          lineHeight: "1.4",
                        }}
                      >
                        <strong>Care:</strong>{" "}
                        {plant.careInstructions.length > 150
                          ? plant.careInstructions.substring(0, 150) + "..."
                          : plant.careInstructions}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommended Supplies */}
          {showRecommendations && (
            <div
              data-pdf-section="supplies"
              style={{
                marginBottom: "30px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "12px 20px",
                  backgroundColor: "#f9fafb",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <h2
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    margin: 0,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Recommended Supplies
                </h2>
              </div>
              {productSections.length > 0 ? (
                <div>
                  {productSections.map((section) => (
                    <div key={section.triggerType}>
                      <p
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#15803d",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          margin: 0,
                          padding: "12px 20px 4px",
                        }}
                      >
                        {section.title}
                      </p>
                      {section.products.map((product) => (
                        <div
                          key={`${section.triggerType}-${product.productId || product.productName}`}
                          data-pdf-avoid-break
                          style={{
                            padding: "10px 20px",
                            borderBottom: "1px solid #f3f4f6",
                            pageBreakInside: "avoid",
                            breakInside: "avoid",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1f2937",
                              margin: 0,
                            }}
                          >
                            {product.productName}
                          </p>
                          {product.description && (
                            <p
                              style={{
                                fontSize: "12px",
                                color: "#4b5563",
                                lineHeight: "1.4",
                                margin: "3px 0 0",
                              }}
                            >
                              {product.description}
                            </p>
                          )}
                          {product.plantNames.length > 0 && (
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                margin: "4px 0 0",
                              }}
                            >
                              <strong>For:</strong>{" "}
                              {product.plantNames.join(", ")}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <ul style={{ margin: 0, padding: "12px 20px 12px 36px" }}>
                  {DEFAULT_SUPPLIES.map((supply, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: "14px",
                        color: "#15803d",
                        marginBottom: "6px",
                      }}
                    >
                      {supply}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Planting Tips / Care Instructions */}
          <div
            data-pdf-section="tips"
            style={{
              padding: "20px",
              backgroundColor: "#fef3c7",
              borderRadius: "8px",
              border: "1px solid #fde68a",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "bold",
                color: "#92400e",
                marginBottom: "12px",
              }}
            >
              PLANTING TIPS
            </h2>
            {Object.keys(careByPlant).length > 0 ? (
              Object.entries(careByPlant).map(
                ([genusId, { plantName, instructions }]) => (
                  <div
                    key={genusId}
                    data-pdf-avoid-break
                    style={{
                      marginBottom: "14px",
                      pageBreakInside: "avoid",
                      breakInside: "avoid",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: "#92400e",
                        margin: "0 0 6px 0",
                      }}
                    >
                      {plantName}
                    </p>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {instructions.map((inst, index) => (
                        <li
                          key={index}
                          style={{
                            fontSize: "14px",
                            color: "#b45309",
                            marginBottom: "6px",
                          }}
                        >
                          {inst.instructionText}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )
            ) : (
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {PLANTING_TIPS.map((tip, index) => (
                  <li
                    key={index}
                    style={{
                      fontSize: "14px",
                      color: "#b45309",
                      marginBottom: "6px",
                    }}
                  >
                    {tip}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Design Coupon Section (rendered at the very end) */}
          {designCoupon &&
            (() => {
              const dc = designCoupon;
              const hasCodeBox =
                !!dc.code && dc.code !== BARCODE_IMAGE_USED_SENTINEL;
              const normalizedType = String(dc.type || "").toLowerCase();
              const isPercentage =
                normalizedType === "percentage" || normalizedType === "percent";
              const discountLine =
                dc.value != null
                  ? isPercentage
                    ? `${dc.value}% off`
                    : `$${dc.value} off`
                  : null;
              const expirationText = dc.relativeExpirationDays
                ? (() => {
                    const expDate = new Date();
                    expDate.setDate(
                      expDate.getDate() + dc.relativeExpirationDays!
                    );
                    return `Offer expires on ${expDate.toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "long", day: "numeric" }
                    )}`;
                  })()
                : "Limited time offer";
              const heading = dc.couponHeading || "Your Exclusive Offer";
              const dcImageSrc = dc.imageUrl
                ? designCouponImageBase64 || dc.imageUrl
                : null;

              return (
                <div
                  data-pdf-section="design-coupon"
                  data-pdf-avoid-break
                  style={{
                    marginTop: "24px",
                    borderRadius: "8px",
                    border: "2px dashed #f59e0b",
                    backgroundColor: "#fffbeb",
                    padding: "18px 20px",
                    textAlign: "center",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#92400e",
                      margin: "0 0 8px 0",
                    }}
                  >
                    {heading}
                  </h3>
                  {discountLine && (
                    <p
                      style={{
                        fontSize: "28px",
                        fontWeight: "900",
                        color: "#b45309",
                        margin: "0 0 6px 0",
                        lineHeight: 1.1,
                      }}
                    >
                      {discountLine}
                    </p>
                  )}
                  {dc.name && (
                    <p
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#92400e",
                        margin: "0 0 8px 0",
                      }}
                    >
                      {dc.name}
                    </p>
                  )}
                  {hasCodeBox && (
                    <p
                      style={{
                        fontSize: "22px",
                        fontWeight: "900",
                        color: "#7c2d12",
                        fontFamily: "monospace",
                        letterSpacing: "4px",
                        margin: "6px 0",
                        padding: "6px 16px",
                        backgroundColor: "#ffffff",
                        borderRadius: "6px",
                        display: "inline-block",
                        border: "1px solid #fde68a",
                      }}
                    >
                      {dc.code}
                    </p>
                  )}
                  {dc.minPurchaseRequirement != null && (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#92400e",
                        margin: "4px 0 0 0",
                      }}
                    >
                      Minimum purchase: ${dc.minPurchaseRequirement}
                    </p>
                  )}
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#92400e",
                      margin: "2px 0 0 0",
                    }}
                  >
                    {expirationText}
                  </p>
                  {dcImageSrc && (
                    <img
                      src={dcImageSrc}
                      alt={dc.name || heading}
                      style={{
                        maxWidth: "320px",
                        width: "100%",
                        height: "auto",
                        marginTop: "12px",
                        borderRadius: "6px",
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                    />
                  )}
                </div>
              );
            })()}

          {/* Footer */}
          <div
            data-pdf-section="footer"
            style={{
              marginTop: "30px",
              paddingTop: "20px",
              borderTop: "1px solid #e5e7eb",
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                margin: 0,
              }}
            >
              🌿 Happy Gardening! • {branding.companyName}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
